import { useEffect, useState } from "react";
import { api, formatCurrency, formatError } from "../lib/api";
import { PageHeader, Modal } from "../components/ui-kit";
import { Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = {
  code: "", name: "", category: "", stock: 0, price: 0,
  is_equipment: false, serial_number: ""
};

export default function MaterialsPage() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = () => {
    api.get("/materials").then((r) => setItems(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setError(""); setOpen(true); };
  const openEdit = (m) => { setEditing(m.id); setForm({ ...EMPTY, ...m }); setError(""); setOpen(true); };

  const save = async () => {
    setError("");
    try {
      const payload = {
        ...form,
        stock: Number(form.stock) || 0,
        price: Number(form.price) || 0,
      };
      if (editing) await api.put(`/materials/${editing}`, payload);
      else await api.post("/materials", payload);
      setOpen(false); load();
    } catch (e) { setError(formatError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este material?")) return;
    await api.delete(`/materials/${id}`);
    load();
  };

  const filtered = items.filter((m) =>
    (!filter || m.category === filter) &&
    (!q || m.name.toLowerCase().includes(q.toLowerCase()) || m.code.toLowerCase().includes(q.toLowerCase()) || (m.serial_number || "").toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div data-testid="materials-page">
      <PageHeader
        eyebrow="Estoque"
        title="Materiais & Equipamentos"
        actions={
          <button className="btn btn-primary" onClick={openNew} data-testid="new-material-btn">
            <Plus size={16} /> Novo Material
          </button>
        }
      />

      <div className="card mb-4 p-4 flex gap-3 flex-wrap">
        <input
          className="input flex-1 min-w-[220px]"
          placeholder="Buscar por nome, código ou nº de série..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="materials-search"
        />
        <select className="select w-56" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="materials-filter-cat">
          <option value="">Todas categorias</option>
          {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
      </div>

      <div className="card">
        <table className="data">
          <thead>
            <tr>
              <th>Código</th><th>Nome</th><th>Categoria</th><th>Nº Série</th><th>Estoque</th><th>Preço</th><th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.id} data-testid={`material-row-${m.code}`}>
                <td className="mono">{m.code}</td>
                <td>
                  {m.name}
                  {m.is_equipment && <span className="ml-2 badge badge-role-gestor">Equipamento</span>}
                </td>
                <td>{m.category || "-"}</td>
                <td className="mono">{m.serial_number || "-"}</td>
                <td className="mono" style={{ color: m.stock < 10 ? "#FACC15" : "inherit" }}>{m.stock}</td>
                <td className="mono">{formatCurrency(m.price)}</td>
                <td>
                  <div className="flex gap-1">
                    <button className="btn btn-ghost" onClick={() => openEdit(m)} data-testid={`edit-material-${m.code}`}><Pencil size={14} /></button>
                    <button className="btn btn-ghost" onClick={() => remove(m.id)} data-testid={`delete-material-${m.code}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-sm" style={{ color: "var(--text-mute)" }}>Nenhum material cadastrado.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={open} onClose={() => setOpen(false)}
        title={editing ? "Editar Material" : "Novo Material"}
        testid="material-modal"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setOpen(false)}>Cancelar</button>
            <button className="btn btn-primary" onClick={save} data-testid="save-material-btn">Salvar</button>
          </>
        }
      >
        {error && <div className="mb-3 p-2 text-sm border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>{error}</div>}
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label-caps block mb-1">Código</label><input className="input mono" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="material-code" /></div>
          <div><label className="label-caps block mb-1">Nome</label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="material-name" /></div>
          <div>
            <label className="label-caps block mb-1">Categoria</label>
            <select className="select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="material-category">
              <option value="">Selecione</option>
              {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div><label className="label-caps block mb-1">Estoque</label><input className="input mono" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="material-stock" /></div>
          <div><label className="label-caps block mb-1">Preço unitário (€)</label><input className="input mono" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="material-price" /></div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_equipment} onChange={(e) => setForm({ ...form, is_equipment: e.target.checked })} data-testid="material-is-equipment" />
              <span className="text-sm">É equipamento (com nº de série)</span>
            </label>
          </div>
          <div className="col-span-2">
            <label className="label-caps block mb-1">Nº de Série {form.is_equipment && <span style={{ color: "var(--primary)" }}>*</span>}</label>
            <input
              className="input mono"
              value={form.serial_number || ""}
              onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
              placeholder="Ex: SN-ABCD1234"
              data-testid="material-serial"
            />
            <div className="text-xs mt-1" style={{ color: "var(--text-mute) " }}>
              Para equipamentos individuais, informe o número de série gravado no aparelho.
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
